'use client';

import Script from 'next/script';

declare global {
  interface Window {
    _paq: unknown[];
  }
}

interface MatomoAnalyticsProps {
  url: string;
  siteId: string;
}

// Normalize to a single trailing slash so `${u}matomo.php` resolves regardless of
// how the env var was written.
function normalizeUrl(url: string): string {
  return url.replace(/\/+$/, '') + '/';
}

// Matomo Cloud serves the tracker JS from its CDN (cdn.matomo.cloud/<host>/matomo.js),
// while the tracking endpoint (matomo.php) stays on the instance. Self-hosted Matomo
// serves both from the instance URL. Match the platform's official snippet for Cloud,
// fall back to the instance path otherwise.
function trackerScriptSrc(u: string): string {
  const host = u.replace(/^https?:\/\//, '').replace(/\/$/, '');
  return host.endsWith('.matomo.cloud')
    ? `https://cdn.matomo.cloud/${host}/matomo.js`
    : `${u}matomo.js`;
}

export default function MatomoAnalytics({ url, siteId }: MatomoAnalyticsProps) {
  if (!url || !siteId) {
    return null;
  }

  const u = normalizeUrl(url);
  const scriptSrc = trackerScriptSrc(u);

  return (
    <Script
      id="matomo-analytics"
      strategy="lazyOnload"
      dangerouslySetInnerHTML={{
        __html: `
          var _paq = window._paq = window._paq || [];
          _paq.push(['trackPageView']);
          _paq.push(['enableLinkTracking']);
          (function() {
            var u="${u}";
            _paq.push(['setTrackerUrl', u+'matomo.php']);
            _paq.push(['setSiteId', '${siteId}']);
            var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
            g.async=true; g.src='${scriptSrc}'; s.parentNode.insertBefore(g,s);
          })();
        `,
      }}
    />
  );
}

// Mirrors updateGoogleConsent / updateMetaConsent. Not strictly required because
// AnalyticsLoader mount-gates Matomo on analytics consent (like Clarity), but kept
// for symmetry so CookieConsentContext can flip Matomo's own consent if wired later.
export function updateMatomoConsent(granted: boolean) {
  if (typeof window === 'undefined' || !Array.isArray(window._paq)) return;
  window._paq.push([granted ? 'setConsentGiven' : 'forgetConsentGiven']);
}

export function trackMatomoEvent(category: string, action: string, name?: string, value?: number) {
  if (typeof window === 'undefined' || !Array.isArray(window._paq)) return;
  window._paq.push(['trackEvent', category, action, name, value]);
}

interface MatomoEcommerceItem {
  sku: string;
  name?: string;
  category?: string;
  price?: number;
  quantity?: number;
}

// Matomo ecommerce order. Items are optional - when omitted the order is still
// recorded with its grand total. `currency` is accepted for signature parity with
// trackMetaPurchase; Matomo carries currency at the site level so it is not sent here.
export function trackMatomoPurchase(
  orderId: string,
  value: number,
  currency: string,
  items?: MatomoEcommerceItem[],
) {
  if (typeof window === 'undefined' || !Array.isArray(window._paq)) return;

  (items ?? []).forEach((item) => {
    window._paq.push([
      'addEcommerceItem',
      item.sku,
      item.name ?? item.sku,
      item.category ?? '',
      item.price ?? 0,
      item.quantity ?? 1,
    ]);
  });

  window._paq.push(['trackEcommerceOrder', orderId, value]);
}
